import { saveTemplateProduct, getTemplateProduct } from '@/api/templateProduct';
import { uploadImageToS3 } from '@/utils/imageUtils';
import { pixelsToMm } from '@/utils/unitsUtils';
import { SaveAs } from '@/typings/designStudio/saveAs';
import MoonPhaseCalculator from '@customily/moon-phase-calculator';
import { IndexDbService } from '../../services/util/indexDbService';
import { tryParseJson } from '@/utils/objectUtils';
import { v4 as uuidv4 } from 'uuid';
import { ColorImagesService } from '@/utils/colorImagesService';
import { dropImageReplicaTestValues } from '@/utils/imagePatternReplicas';

const isPrintSizeVariant = 'isprintsizevariant';
const isColorOptionVariant = 'iscoloroptionvariant';

(function () {
    'use strict';
    'ngInject';

    angular.module('app.ctrls').controller('CreateStepThreeCtrl', createStepThreeCtrl);

    function createStepThreeCtrl(
        $scope,
        $rootScope,
        $location,
        $mdDialog,
        $timeout,
        $sce,
        ProductService,
        blockUI,
        DesignStudioSaveService,
        TemplatePermissionsService,
        AppCuesService,
        DynamicImageService
    ) {
        const printAreaUrl = '/Content/assets/print-area-customily.png';
        const colorVariantNames = ['color', 'colors'];
        $scope.productSaved = false;
        $scope.selectedElement = null;
        $scope.elementProperties = {};
        $scope.layersInstance = null;
        $scope.pageEpsInstance = null;
        $scope.willUpdateOptionSets = ProductService.getProductWillUpdateOptionSets();

        // Initialize template permissions
        $scope.canSave = true;
        $scope.saveDisabledText = '';

        TemplatePermissionsService.initTemplatePermissionSteps($scope);

        $scope.saveTemplateDetails = {
            isOpen: false,
            title: 'Template Saved',
            productTitle: '',
            productId: ''
        };

        $scope.pageEpsProps = {
            enable: true,
            collapseTitle: 'Select a File to review'
        };

        $scope.customilyMoonPhase = {
            date: new Date()
        };

        $scope.IndexDbService = new IndexDbService();

        $scope.qrConfig = {
            text: ''
        };

        $scope.fromListingNextPath = '/store/podSelection';
        const forProductTemplateCreation = !!$location.search().forProductTemplateCreation;
        const isUserDesignCreation = !!$location.search().forUserDesignCreation;
        const forMyProductBases = !!$location.search().forMyProductBases || false;
        const flowId = $location.search().flowId;
        $scope.fromListing = !!$location.search().fromListing || false;
        $scope.fromPreview = !!$location.search().fromPreview || false;
        $scope.showTemplateOptionSetSyncToggle =
            !forProductTemplateCreation &&
            !isUserDesignCreation &&
            !forMyProductBases &&
            $rootScope?.user?.AccessRoles?.Serialized?.split(',')?.includes('enable-template-option-set-sync') &&
            ProductService.getProduct()?.LinkedToStoreProduct;

        $scope.updateFileDetails = function () {
            const product = ProductService.getProduct();
            const eps = product?.listEPS?.[0];
            if (!eps) {
                return;
            }
            $scope.productBaseResume = {
                fileWidth: Number(pixelsToMm(eps.width, eps.dpi).toFixed(2)),
                fileHeight: Number(pixelsToMm(eps.height, eps.dpi).toFixed(2)),
                unit: 'mm',
                dpi: eps.dpi,
                format: product.format,
                variants: $rootScope.allOptions
            };
        };

        $scope.initializeProductFromListing = function () {
            const previewWidth = 1000; //px
            const previewHeight = 1000; //px
            const dpi = 300;
            const measureUnit = 'mm';
            const exportFormat = 'png';
            const exportColorMode = 'rgb';
            const parameters = $rootScope.productBaseFromListing;
            const uuid = uuidv4();

            // Set product properties
            ProductService.setProductId(uuid);
            ProductService.setEditMode(false);
            ProductService.setProductName(parameters.title);
            ProductService.setProductFormat(exportFormat);
            ProductService.setProductColorMode(exportColorMode);
            ProductService.setMelcoReferenceId(null);

            const { fileWidth, fileHeight, exportPPI } = parameters.printAreaConfig;
            // Add eps
            const eps = {
                id: 1,
                uuid: uuidv4(),
                locked: false,
                visible: true,
                name: parameters.title,
                type: 'eps',
                zIndex: 0,
                zIndexPreview: null,
                width: fileWidth,
                height: fileHeight,
                measureUnit,
                dpi,
                exportPPI: exportPPI,
                baseImage: '',
                baseEps: '',
                podPlaceholder: null,
                textsEps: [],
                circularTextsEps: [],
                imagePlaceHoldersEps: [],
                vectorEPSEps: []
            };
            ProductService.addListEPS([{ ...eps }]);

            // Create print area image
            const image = DynamicImageService.createDynamicImage(null, 1, {
                left: eps.width / 2,
                top: eps.height / 2,
                width: eps.width,
                height: eps.height,
                zIndex: 1,
                pageEpsId: eps.id,
                name: 'Print Area',
                addedOnEdit: false
            });
            image.isPrintArea = true;
            image.dynamicImagesPath.set(1, printAreaUrl);

            // Add print area to eps
            ProductService.addEPSPlaceholderImage({
                ...image,
                centerX: image.left,
                centerY: image.top,
                rotation: image.angle,
                opacity: 1,
                flipX: false,
                flipY: false,
                rotationStep: 1,
                textToImage: false
            });

            // Set preview values
            ProductService.setPreviewImage(null);
            ProductService.setPreviewBgColor('rgba(255, 255, 255, 1)');
            ProductService.setPreviewImgSize(previewWidth, previewHeight);

            // Add print area in preview
            const { width, height, left, top, rotation, imageWidth, imageHeight } = parameters.printAreaConfig;
            // Scale the dimensions and position returned by Gemini to a 1000x1000 px preview
            const scale = Math.min(previewWidth / imageWidth, previewHeight / imageHeight);
            const offsetX = (previewWidth - imageWidth * scale) / 2;
            const offsetY = (previewHeight - imageHeight * scale) / 2;

            const scaledWidth = width * scale;
            const scaledHeight = height * scale;
            const scaledLeft = left * scale;
            const scaledTop = top * scale;

            // Add print area to preview
            ProductService.addPreviewPlaceholderImage({
                ...image,
                width: scaledWidth,
                height: scaledHeight,
                centerX: offsetX + scaledLeft + scaledWidth / 2,
                centerY: offsetY + scaledTop + scaledHeight / 2,
                rotation: rotation,
                opacity: 1,
                flipX: false,
                flipY: false,
                skewX: 0,
                skewY: 0,
                addedOnEdit: false,
                rotationStep: 1,
                textToImage: false
            });

            // Create product colors image
            const prodColorImage = DynamicImageService.createDynamicImage(null, 2, {
                left: previewWidth / 2,
                top: previewHeight / 2,
                width: previewWidth,
                height: previewHeight,
                zIndex: 0,
                pageEpsId: null,
                name: 'Product images',
                addedOnEdit: false,
                isColorOptions: true
            });
            prodColorImage.dynamicImagesPath.set(1, {
                path: parameters.baseImagePath,
                fileName: 'base-image'
            });
            // Add product color image to preview
            ProductService.addPreviewPlaceholderImage({
                ...prodColorImage,
                centerX: prodColorImage.left,
                centerY: prodColorImage.top,
                rotation: prodColorImage.angle,
                opacity: 1,
                flipX: false,
                flipY: false,
                skewX: 0,
                skewY: 0,
                addedOnEdit: false,
                rotationStep: 1,
                textToImage: false
            });

            //Rename variant color name to "Color", which is the one accepted in our app (to change the color image)
            parameters.options = (parameters.options ?? []).map((opt) => {
                if (colorVariantNames.includes(opt.name.toLowerCase())) {
                    return {
                        ...opt,
                        name: 'Color'
                    };
                }
                return opt;
            });

            $rootScope.colorVariants =
                parameters.options.find((opt) => colorVariantNames.includes(opt.name.toLowerCase()))?.values || [];
            $rootScope.allOptions = parameters.options.map((opt) => ({
                ...opt,
                isColorVariant: colorVariantNames.includes(opt.name.toLowerCase())
            }));
            $rootScope.productImagePath = parameters.baseImagePath;

            // Set product base info
            const templateProduct = ProductService.getProduct();
            const variants = parameters.options.map((opt) => ({
                name: opt.name,
                value: JSON.stringify(opt.values)
            }));
            ProductService.loadProduct({
                ...templateProduct,
                templateProductVariants: variants
            });
            const myProductBasesId = 5;
            ProductService.setPod({ id: myProductBasesId, apiKeyReference: '', isUserPod: true });
        };

        if ($scope.fromListing && $rootScope.productBaseFromListing) {
            if (!$scope.fromPreview) {
                $scope.initializeProductFromListing();
            }
            $scope.updateFileDetails();
        }

        // Set header title
        $rootScope.headerTitle = 'Step 3/3 - Template Review - ' + ProductService.getProduct().name;
        $scope.shProduct = $rootScope.shProduct;
        $scope.showOptionSetCreationToggle = !!$scope.shProduct;
        $rootScope.headerMode = 'single';

        if ($scope.shProduct) $scope.shProduct.createOptionSetFromTemplate = true;

        $scope.$watch('productSaved', (newValue) => {
            if (!newValue) return;
            ProductService.clearProduct();
        });

        $scope.onCreateOptionSetFromTemplate = function ($event) {
            $scope.shProduct.createOptionSetFromTemplate = $event;
        };

        $scope.onProductWillUpdateOptionSetsChange = function ($event) {
            $scope.willUpdateOptionSets = $event;
        };

        $scope.broadcastEvent = function (data) {
            setTimeout(() => {
                $rootScope.$broadcast('canvas:callback', data);
            }, 0);
        };

        $scope.updateLayersOrderCanvas = function () {
            setTimeout(() => {
                $rootScope.$broadcast('canvas:update-layers-order');
            }, 0);
        };

        $scope.updateText = function (text, element) {
            AppCuesService.proceedAfterDoneTyping();

            const data = {
                text: text,
                id: element.id
            };

            $rootScope.$broadcast('updateText:stepOne', data);
            $rootScope.$broadcast('updateText:stepTwo', data);
        };

        $scope.updateFontOption = function (fontOption, element) {
            const data = {
                fontOption: fontOption,
                id: element.id
            };

            $rootScope.$broadcast('updateTextFont:stepOne', data);
            $rootScope.$broadcast('updateTextFont:stepTwo', data);
        };

        $scope.updateFontColorOption = function (fontColorOption, element) {
            const data = {
                fontColorOption: fontColorOption,
                id: element.id
            };

            $rootScope.$broadcast('updateTextFontColor:stepOne', data);
            $rootScope.$broadcast('updateTextFontColor:stepTwo', data);
        };

        $scope.handleCalculateMoonPhase = function () {
            const date = $scope.customilyMoonPhase.date;
            const utcNoon = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 12));
            const phase = MoonPhaseCalculator.handleCalculateMoonPhase(utcNoon);
            $scope.onSelectPhase(phase);
        };

        $scope.onSelectPhase = function (option) {
            $scope.elementProperties.imageOption = option;
            $scope.updateImageOption($scope.elementProperties.imageOption, $scope.selectedElement);
        };

        $scope.updateImageOption = function (imageOption, element) {
            const data = {
                option: imageOption,
                id: element.id
            };

            $rootScope.$broadcast('updateDynamicElementOption:stepOne', data);
            $rootScope.$broadcast('updateDynamicElementOption:stepTwo', data);
        };

        $scope.updateVectorOption = function (vectorOption, element) {
            const data = {
                option: vectorOption,
                id: element.id
            };

            $rootScope.$broadcast('updateDynamicElementOption:stepOne', data);
            $rootScope.$broadcast('updateDynamicElementOption:stepTwo', data);
        };

        $scope.isTextToImagePromptActive = false;
        $scope.setIsTextToImagePromptActive = function (value) {
            $scope.isTextToImagePromptActive = value;
        };

        $scope.uploadImageOnPlaceholder = function (element, view) {
            if (view.files && view.files[0]) {
                const data = {
                    file: view.files[0],
                    id: element.id
                };
                view.value = null;

                $rootScope.$broadcast('uploadImagePlaceholder:stepOne', data);
                $rootScope.$broadcast('uploadImagePlaceholder:stepTwo', data);
            }
        };

        $scope.generateTextToImageTest = function (promptText) {
            const element = $scope.selectedElement;
            if (!element || !promptText) return;

            const data = {
                ids: [element.id],
                prompt: promptText
            };

            $rootScope.$broadcast('generateTextToImageTest:stepOne', data);
            $rootScope.$broadcast('generateTextToImageTest:stepTwo', data);
        };

        $scope.resetImageTestValue = async function () {
            $rootScope.$broadcast('resetImageTestValue:stepOne');
            $rootScope.$broadcast('resetImageTestValue:stepTwo');
        };

        $scope.uploadSvgOnPlaceholder = function (element, view) {
            if (view.files && view.files[0]) {
                const data = {
                    file: view.files[0],
                    id: element.id
                };
                view.value = null;

                $rootScope.$broadcast('uploadVectorPlaceholder:stepOne', data);
                $rootScope.$broadcast('uploadVectorPlaceholder:stepTwo', data);
            }
        };

        $scope.trustedTestSvg = '';

        $scope.resetSvgTestValue = async function () {
            $scope.trustedTestSvg = '';
            $rootScope.$broadcast('resetSvgTestValue:stepOne');
            $rootScope.$broadcast('resetSvgTestValue:stepTwo');
        };

        $scope.$watch('selectedElement.svgString', function (newVal) {
            if (newVal && !/^<!--\s*default customily vector\s*-->/.test(newVal.trim())) {
                $scope.trustedTestSvg = $sce.trustAsResourceUrl(
                    'data:image/svg+xml;utf8,' + encodeURIComponent(newVal)
                );
            } else {
                $scope.trustedTestSvg = '';
            }
        });

        $scope.updateImageColor = function (option, element) {
            const data = {
                colorOption: option,
                id: element.id
            };

            $rootScope.$broadcast('updateImageColorOption:stepOne', data);
            $rootScope.$broadcast('updateImageColorOption:stepTwo', data);
        };

        $scope.updateVectorColor = function (option, element) {
            const data = {
                colorOption: option,
                id: element.id
            };

            $rootScope.$broadcast('updateVectorColorOption:stepOne', data);
            $rootScope.$broadcast('updateVectorColorOption:stepTwo', data);
        };

        $scope.updateQrText = function (qrText, element) {
            const data = {
                qrText: qrText,
                id: element.id
            };
            $rootScope.$broadcast('updateQrText:stepOne', data);
            $rootScope.$broadcast('updateQrText:stepTwo', data);
        };

        $scope.updateSelectedElement = function (element) {
            $scope.selectedElement = element;
            if (element) {
                $scope.elementProperties.text = element.getText ? element.getText() : element.text;
                $scope.elementProperties.fontOption = element.fontOption;
                $scope.elementProperties.fontColorOption = element.fontColorOption;
                $scope.elementProperties.imageOption = element.dynamicImageVariation;
                $scope.elementProperties.colorPosition = element.selectedColorPosition;
                $scope.elementProperties.fullColor = element.fullColor;
                $scope.elementProperties.grading = element.grading;
                $scope.elementProperties.selectedPosition = element.selectedPosition;
                $scope.elementProperties.applyColoring = element.applyColoring;
                $scope.elementProperties.prefix = element.prefix;
                $scope.elementProperties.suffix = element.suffix;
                $scope.elementProperties.betweenCharacters = element.betweenCharacters;
                $scope.elementProperties.betweenWords = element.betweenWords;
                $scope.elementProperties.referenceId = element.referenceId;
            }
        };

        $scope.$on('canvas:previewImageGenerated:stepTwo', async function (event, data) {
            const dataUrl = data.dataUrl;
            if (dataUrl) {
                const response = await uploadImageToS3('thumbnail', dataUrl);
                $scope.processSaveTemplate(response);
            }
        });

        $scope.$on('canvas:selection:created', async function (event, data) {
            const dataUrl = data.dataUrl;
            if (dataUrl) {
                const response = await uploadImageToS3('thumbnail', dataUrl);
                $scope.processSaveTemplate(response);
            }
        });

        $scope.save = function () {
            let msg = 'Saving template';

            if (forProductTemplateCreation) {
                const pod = ProductService.getPod();
                if (pod && !['printful', 'printify'].includes(pod.apiKeyReference)) {
                    $scope.saveTemplateProductManual();
                    return false;
                }
            } else if (isUserDesignCreation) {
                msg = 'Saving design';
            }
            blockUI.start(msg);
            //HERE WE HAVE SOME FORMATTING BEFORE SAVING
            $scope.imagePlaceHoldersFormated();
            $scope.vectorsFormated();
        };

        $scope.editProductionFile = function () {
            $location.path('/createProduct1').search({
                fromListing: $location.search().fromListing,
                forProductTemplateCreation: 'true',
                forMyProductBases: 'true'
            });
        };

        $scope.editPreview = function () {
            $location.path('/createProduct2').search({
                fromListing: $location.search().fromListing,
                forProductTemplateCreation: 'true',
                forMyProductBases: 'true'
            });
        };

        $scope.colorVariantSelected = function (position) {
            const preview = ProductService.getPreview();
            const colorOption = preview.imagePlaceHoldersPreview.find((item) => item.isColorOptions);
            if (!colorOption) {
                return;
            }
            $rootScope.$broadcast('updateDynamicElementOption:stepTwo', { id: colorOption.id, option: position });
        };

        $scope.generateColorImages = async function () {
            if (!$rootScope.productImagePath || !$rootScope.colorVariants?.length) {
                return;
            }

            try {
                $scope.showBlockUiLoader(true, 'Generating image colors 📝✨🎨');
                // Generate product color images
                const colorImages = await ColorImagesService.generate({
                    imagePath: $rootScope.productImagePath.slice('/Content/'.length),
                    colors: $rootScope.colorVariants
                });

                // Update dynamicImagesPath
                const preview = ProductService.getPreview();
                const colorOption = preview.imagePlaceHoldersPreview.find((item) => item.isColorOptions);
                const dynamicImagesMap = new Map();
                let position = 1;
                for (const image of colorImages) {
                    dynamicImagesMap.set(position, {
                        path: image.path,
                        fileName: image.color
                    });
                    position++;
                }
                colorOption.dynamicImagesPath = JSON.stringify([...dynamicImagesMap]);

                // Update image on step 2 canvas
                const data = {
                    id: colorOption.id,
                    dynamicImagesPath: dynamicImagesMap,
                    option: 1
                };
                $rootScope.$broadcast('updateDynamicImage:stepTwo', data);
            } catch (error) {
                $mdDialog.show(
                    $mdDialog
                        .alert()
                        .parent(document.body)
                        .clickOutsideToClose(true)
                        .title('We received an error:')
                        .htmlContent(error.message)
                        .ok('OK!')
                );
            } finally {
                $scope.showBlockUiLoader(false);
                $timeout(() => $scope.$apply());
            }
        };

        $scope.getVariantSize = function (width, height) {
            if (width === undefined || height === undefined) return '';
            const DPI = ProductService.getEps()?.dpi || 300;
            return `${Number(pixelsToMm(width, DPI)).toFixed(2)} x ${Number(pixelsToMm(height, DPI)).toFixed(2)} mm`;
        };

        $scope.templateProductSaveModal = {
            isOpen: false,
            variants: [],
            canAddPrintSize: false,
            displayDescription: true,
            printSizeVariantOption: {},
            isChildTemplateProduct: false
        };
        $scope.saveTemplateProductManual = function () {
            const templateProduct = ProductService.getProduct();
            const listPreview = ProductService.getListPreview();
            const childTemplateProduct = ProductService.getChildTemplateProduct();

            $scope.templateProductSaveModal.displayDescription = ProductService.getPod().isUserPod === undefined;
            $scope.templateProductSaveModal.isOpen = true;
            $scope.templateProductSaveModal.canAddPrintSize =
                templateProduct.listEPS.length == 1 && !$scope.fromListing && listPreview.length <= 1;
            $scope.templateProductSaveModal.isChildTemplateProduct = !!childTemplateProduct?.parentId;

            if (!childTemplateProduct?.parentId) {
                const productImageLibraryDetails = ProductService.getProductColorsLibraryDetails();
                let libraryElements = [];

                if (productImageLibraryDetails) {
                    libraryElements = productImageLibraryDetails.elements
                        .map((libraryOption) => ({
                            name: libraryOption.name || `Option ${libraryOption.position}`,
                            hint: ''
                        }))
                }
                const isLibraryBacked = libraryElements.length > 0;
                $scope.templateProductSaveModal.canAddPrintSize = $scope.templateProductSaveModal.canAddPrintSize && !isLibraryBacked;
                const colorPlaceholder =
                    templateProduct.preview?.imagePlaceHoldersPreview?.find((item) => item.isColorOptions) ??
                    listPreview?.find((p) => p.isDefault)?.imagePlaceHoldersPreview?.find((item) => item.isColorOptions) ??
                    listPreview?.[0]?.imagePlaceHoldersPreview?.find((item) => item.isColorOptions);
                let uploadColorTupleCount = 0;
                try {
                    uploadColorTupleCount = JSON.parse(colorPlaceholder?.dynamicImagesPath || '[]').length;
                } catch (e) {
                    uploadColorTupleCount = 0;
                }
                const isUploadBackedColor = !isLibraryBacked && uploadColorTupleCount > 1;
                const dbVariants = templateProduct.templateProductVariants;
                $scope.templateProductSaveModal.variants =
                    (templateProduct.ProductId || $scope.fromListing) && dbVariants
                        ? dbVariants.map((v, variantOptionIndex) => {
                              const isPrintSize = v.name.includes(isPrintSizeVariant);
                              const isColorOption = variantOptionIndex === 0 && (isUploadBackedColor || isLibraryBacked);
                              return {
                                  isPrintSizeVariant: isPrintSize,
                                  isColorOptionVariant: isColorOption,
                                  readOnlyValues: isColorOption && isLibraryBacked,
                                  name: v.name.split(isPrintSizeVariant)[0].split(isColorOptionVariant)[0],
                                  values: isColorOption && isLibraryBacked ? libraryElements : JSON.parse(v.value).map((value) => {
                                      const parsedValue = isPrintSize ? tryParseJson(value) : { name: value };
                                      return {
                                          ...parsedValue,
                                          name: parsedValue.name || parsedValue.variant,
                                          hint: $scope.getVariantSize(parsedValue.width, parsedValue.height)
                                      };
                                  })
                              };
                          })
                        : [
                              {
                                  name: 'Color',
                                  isColorOptionVariant: isLibraryBacked || isUploadBackedColor,
                                  readOnlyValues: isLibraryBacked,
                                  values: libraryElements.length ? libraryElements : $scope.getDynamicImagesFromProductColor()
                              }
                          ];
            } else {
                $scope.templateProductSaveModal.variants = [
                    {
                        name: childTemplateProduct.newPrintVariantName?.length
                            ? childTemplateProduct.newPrintVariantName.split(isPrintSizeVariant)[0]
                            : 'Size',
                        isPrintSizeVariant: true,
                        values: [
                            ...childTemplateProduct.savedTemplates.map((v) => ({
                                name: v.newVariantValue,
                                hint: v.oldVariantValue
                            })),
                            {
                                name: childTemplateProduct.currentTemplate.newVariant,
                                hint: childTemplateProduct.currentTemplate.oldVariant
                            }
                        ]
                    }
                ];
            }
        };

        $scope.saveTemplateProduct = async function (data) {
            blockUI.start('Saving template product');
            $scope.templateProductSaveModal.isOpen = false;
            if (data.description != undefined) {
                ProductService.setDescription(data.description);
            }
            const oldChildTemplateProduct = ProductService.getChildTemplateProduct();
            const templateProduct = ProductService.getProduct();

            let templateProductVariants = [];
            let childrenTemplateProductsVariants = ProductService.getTemplateProductVariants() ?? [];
            if ($scope.fromListing) {
                childrenTemplateProductsVariants = [];
            }

            const parentPrintSizeVariants =
                templateProduct.templateProductVariants?.find((v) => v.name.includes(isPrintSizeVariant)) ?? undefined;
            const parentTemplateProductEdition =
                typeof parentPrintSizeVariants !== 'undefined' &&
                !$scope.templateProductSaveModal.isChildTemplateProduct;

            const sizeVariantOption = data.variants.find((v) => v.isPrintSizeVariant);

            const childTemplateProduct = $scope.storeChildTemplateProductParameters(
                {
                    ...oldChildTemplateProduct,
                    isCompleted: !data.partialSave,
                    forChildTemplateProductCreation: data.partialSave
                        ? !parentTemplateProductEdition
                        : oldChildTemplateProduct?.forChildTemplateProductCreation || false,
                    parentTemplateProductEdition: parentTemplateProductEdition
                },
                parentPrintSizeVariants,
                sizeVariantOption?.values
            );

            if (
                childTemplateProduct?.savedTemplates &&
                childTemplateProduct?.forChildTemplateProductCreation &&
                !parentTemplateProductEdition
            ) {
                const parentTemplateProduct = childTemplateProduct.parentId
                    ? await getTemplateProduct(childTemplateProduct.parentId)
                    : null;
                if (parentTemplateProduct) {
                    ProductService.setProductName(
                        `${parentTemplateProduct.name + ' - ' + childTemplateProduct.currentTemplate.newVariant}`
                    );
                }
                const printSizeVariants = childTemplateProduct.savedTemplates.map(
                    (template) => template.newVariantValue
                );

                data.variants.push({
                    name: childTemplateProduct.newPrintVariantName + isPrintSizeVariant,
                    values: [{ name: printSizeVariants.toString() }]
                });
                if (childrenTemplateProductsVariants.length && childTemplateProduct.parentId) {
                    childrenTemplateProductsVariants = childrenTemplateProductsVariants.map((v) => {
                        if (!v.name.toLocaleLowerCase().includes(isPrintSizeVariant.toLocaleLowerCase())) return v;

                        const printSizesParsed = JSON.parse(v.value).map((p, index) => ({
                            ...p,
                            variant: printSizeVariants[index]
                        }));
                        printSizesParsed.push(printSizeVariants[printSizeVariants.length - 1]);
                        return {
                            name: childTemplateProduct.newPrintVariantName + isPrintSizeVariant,
                            value: JSON.stringify(printSizesParsed)
                        };
                    });
                }
            }

            for (const option of data.variants) {
                if (option.isPrintSizeVariant) continue;
                const optionValue = option.values.map((o) => o.name.trim());
                templateProductVariants.push({
                    name: option.isColorOptionVariant ? option.name + isColorOptionVariant : option.name,
                    value: JSON.stringify(optionValue)
                });
            }

            if (parentTemplateProductEdition && sizeVariantOption && sizeVariantOption.values.length > 1) {
                const values = sizeVariantOption.values.map((v) => {
                    const value = { ...v };
                    delete value.name;
                    delete value.hint;
                    return { ...value, variant: v.name || value.variant };
                });
                templateProductVariants.push({
                    name: `${sizeVariantOption.name}${isPrintSizeVariant}`,
                    value: JSON.stringify(values)
                });
            }

            ProductService.setTemplateProductVariants(
                childrenTemplateProductsVariants.length ? childrenTemplateProductsVariants : templateProductVariants
            );

            $scope.imagePlaceHoldersFormated();
            $scope.vectorsFormated();
        };

        $scope.getDynamicImagesFromProductColor = function () {
            const productColorsFileNames = ProductService.getProductColorsFilenames();
            let result = '';
            try {
                const filenames = productColorsFileNames.map((option) => {
                    const index = option.name.lastIndexOf('.');
                    return index !== -1 ? option.name.substring(0, index) : option.name;
                });
                result = [...new Set(filenames)].map((name) => ({ name, hint: '' }));
            } catch (err) {
                window.rumReportError(error);
            }
            return result;
        };

        $scope.imagePlaceHoldersFormated = function () {
            const listPreview = ProductService.getListPreview();
            const listEps = ProductService.getListEPS();

            //VERIFYING THAT THE DYNAMIC IMAGES ARE STRINGIFIED
            for (const eps of listEps) {
                eps.imagePlaceHoldersEps.forEach((imgPlaceholder) => {
                    if (imgPlaceholder.dynamicImagesPath && typeof imgPlaceholder.dynamicImagesPath !== 'string') {
                        imgPlaceholder.dynamicImagesPath = JSON.stringify(
                            Array.from(imgPlaceholder.dynamicImagesPath.entries())
                        );
                    } else if (imgPlaceholder.mapConfig && typeof imgPlaceholder.mapConfig !== 'string') {
                        imgPlaceholder.mapConfig = JSON.stringify(imgPlaceholder.mapConfig);
                    } else if (imgPlaceholder.wordCloudConfig && typeof imgPlaceholder.wordCloudConfig !== 'string') {
                        imgPlaceholder.wordCloudConfig = JSON.stringify(imgPlaceholder.wordCloudConfig);
                    } else if (imgPlaceholder.calendarConfig && typeof imgPlaceholder.calendarConfig !== 'string') {
                        imgPlaceholder.calendarConfig = JSON.stringify(imgPlaceholder.calendarConfig);
                    } else if (
                        imgPlaceholder.imageToImageConfig &&
                        typeof imgPlaceholder.imageToImageConfig !== 'string'
                    ) {
                        imgPlaceholder.imageToImageConfig = JSON.stringify(imgPlaceholder.imageToImageConfig);
                    } else if (imgPlaceholder.imageEffects && typeof imgPlaceholder.imageEffects !== 'string') {
                        imgPlaceholder.imageEffects = JSON.stringify(imgPlaceholder.imageEffects);
                    } else if (imgPlaceholder.wordSearchConfig && typeof imgPlaceholder.wordSearchConfig !== 'string') {
                        imgPlaceholder.wordSearchConfig = JSON.stringify(imgPlaceholder.wordSearchConfig);
                    }

                    if (imgPlaceholder.crosswordSettings && typeof imgPlaceholder.crosswordSettings !== 'string') {
                        imgPlaceholder.crosswordSettings = JSON.stringify(imgPlaceholder.crosswordSettings);
                    }
                });
            }

            for (const preview of listPreview) {
                if (!preview) continue;

                preview.imagePlaceHoldersPreview?.forEach((imgPlaceholder) => {
                    if (imgPlaceholder.mapConfig && typeof imgPlaceholder.mapConfig !== 'string') {
                        imgPlaceholder.mapConfig = JSON.stringify(imgPlaceholder.mapConfig);
                    }
                    if (imgPlaceholder.wordCloudConfig && typeof imgPlaceholder.wordCloudConfig !== 'string') {
                        imgPlaceholder.wordCloudConfig = JSON.stringify(imgPlaceholder.wordCloudConfig);
                    }
                    if (imgPlaceholder.calendarConfig && typeof imgPlaceholder.calendarConfig !== 'string') {
                        imgPlaceholder.calendarConfig = JSON.stringify(imgPlaceholder.calendarConfig);
                    }
                    if (imgPlaceholder.imageToImageConfig && typeof imgPlaceholder.imageToImageConfig !== 'string') {
                        imgPlaceholder.imageToImageConfig = JSON.stringify(imgPlaceholder.imageToImageConfig);
                    }
                    if (imgPlaceholder.imageEffects && typeof imgPlaceholder.imageEffects !== 'string') {
                        imgPlaceholder.imageEffects = JSON.stringify(imgPlaceholder.imageEffects);
                    }
                    if (imgPlaceholder.wordSearchConfig && typeof imgPlaceholder.wordSearchConfig !== 'string') {
                        imgPlaceholder.wordSearchConfig = JSON.stringify(imgPlaceholder.wordSearchConfig);
                    }
                    if (imgPlaceholder.crosswordSettings && typeof imgPlaceholder.crosswordSettings !== 'string') {
                        imgPlaceholder.crosswordSettings = JSON.stringify(imgPlaceholder.crosswordSettings);
                    }

                    if (imgPlaceholder.clipPath && typeof imgPlaceholder.clipPath !== 'string') {
                        try {
                            const clipPath = imgPlaceholder.clipPath.toObject
                                ? imgPlaceholder.clipPath.toObject()
                                : imgPlaceholder.clipPath;
                            imgPlaceholder.clipPath = JSON.stringify({
                                ...clipPath,
                                absolutePositioned: imgPlaceholder.clipPath.absolutePositioned
                            });
                        } catch (error) {
                            console.error('Error stringifying clipPath', error);
                        }
                    }
                    if (ProductService.getIsProductColorModified()) imgPlaceholder.podProductImagesMetadata = null;
                });
            }

            $rootScope.$broadcast('canvas:previewImageRequest:stepTwo', {
                size: 512
            });
        };

        $scope.vectorsFormated = function () {
            const listPreview = ProductService.getListPreview();
            const listEps = ProductService.getListEPS();

            for (const eps of listEps) {
                eps.vectorEPSEps.forEach((vector) => {
                    if (vector.starMapConfig && typeof vector.starMapConfig !== 'string') {
                        vector.starMapConfig = JSON.stringify(vector.starMapConfig);
                    }

                    if (vector.qrConfig && typeof vector.qrConfig !== 'string') {
                        vector.qrConfig = JSON.stringify(vector.qrConfig);
                    }
                });
            }

            for (const preview of listPreview) {
                if (!preview) continue;

                preview?.vectorEPSPreview?.forEach((vector) => {
                    if (vector.starMapConfig && typeof vector.starMapConfig !== 'string') {
                        vector.starMapConfig = JSON.stringify(vector.starMapConfig);
                    }

                    if (vector.qrConfig && typeof vector.qrConfig !== 'string') {
                        vector.qrConfig = JSON.stringify(vector.qrConfig);
                    }
                });
            }
        };

        $scope.processSaveTemplate = async function (data) {
            ProductService.setProductWillUpdateOptionSets($scope.willUpdateOptionSets);
            const product = ProductService.getProduct();
            const preview = ProductService.getPreview();
            // Save product thumbnail
            preview.thumbnailPath = data.Path;
            const eps = ProductService.getEps();
            try {
                // Determine which endpoint to used base on if we are saving a product template or if we are just saving a normal template
                if (forProductTemplateCreation) {
                    // Set any POD assignments for this template product
                    product.podAssignments = ProductService.getPodAssignments() || undefined;
                    product.podId = ProductService.getPod().id;
                    product.description = ProductService.getDescription() || null;
                    product.Linked = false;
                    // Set any Template Product Variants for this template product
                    const childTemplateProductParameters = ProductService.getChildTemplateProduct();
                    if (childTemplateProductParameters) {
                        if (
                            childTemplateProductParameters.forChildTemplateProductCreation &&
                            !childTemplateProductParameters.parentTemplateProductEdition
                        ) {
                            ProductService.setTemplateProductVariants(
                                ProductService.getTemplateProductVariants().map((v) => {
                                    if (!v.name.includes(isPrintSizeVariant)) return v;

                                    const printSizesParsed = JSON.parse(v.value);
                                    printSizesParsed[printSizesParsed.length - 1] = {
                                        variant: printSizesParsed[printSizesParsed.length - 1],
                                        templateId: product.id
                                    };
                                    return {
                                        name: v.name,
                                        value: JSON.stringify(printSizesParsed)
                                    };
                                })
                            );
                        }

                        if (childTemplateProductParameters.parentTemplateProductEdition) {
                            const tmp = ProductService.getTemplateProductVariants().find((v) =>
                                v.name.includes(isPrintSizeVariant)
                            );
                            ProductService.setChildTemplateProduct({
                                ...childTemplateProductParameters,
                                savedTemplates: JSON.parse(tmp.value).map((v) => {
                                    const dimension = `${Number(pixelsToMm(v.width, eps.dpi)).toFixed(2)} x ${Number(
                                        pixelsToMm(v.height, eps.dpi)
                                    ).toFixed(2)} mm`;
                                    return {
                                        newVariantValue: v.variant,
                                        newVariant: v.variant,
                                        oldVariantValue: dimension,
                                        oldVariant: dimension
                                    };
                                })
                            });
                        }
                    }
                    product.templateProductVariants = ProductService.getTemplateProductVariants() || undefined;

                    const templateSaved =
                        childTemplateProductParameters && childTemplateProductParameters.parentId
                            ? await saveTemplateProduct({
                                  ...product,
                                  id: product.id,
                                  parentId: childTemplateProductParameters.parentId
                              })
                            : await saveTemplateProduct(product, product.id);

                    blockUI.stop();
                    if ($scope.fromListing && $rootScope.productBaseFromListing) {
                        $rootScope.productBaseFromListing.productBaseId = templateSaved.ProductId;
                        $timeout(() => $scope.$apply());
                        $scope.productSaved = true;
                        $location.path($scope.fromListingNextPath);
                    } else if (
                        childTemplateProductParameters &&
                        childTemplateProductParameters.forChildTemplateProductCreation &&
                        !childTemplateProductParameters.isCompleted
                    ) {
                        const path = '/createProduct1';
                        const parameters = {
                            templateId: templateSaved.id,
                            forProductTemplateCreation: true
                        };
                        const searchParameters = new URLSearchParams(
                            forMyProductBases
                                ? {
                                      ...parameters,
                                      forMyProductBases: true
                                  }
                                : parameters
                        );
                        if (templateSaved.parentId) {
                            ProductService.setChildTemplateProduct({
                                ...ProductService.getChildTemplateProduct(),
                                parentId: templateSaved.parentId
                            });
                        } else if (!childTemplateProductParameters.parentId) {
                            ProductService.setChildTemplateProduct({
                                ...ProductService.getChildTemplateProduct(),
                                parentId: templateSaved.id
                            });
                        }

                        $location.path(`${path}?${searchParameters}`);
                        $location.url($location.path());
                        $timeout(() => $scope.$apply());
                    } else {
                        $mdDialog
                            .show({
                                controller: 'TemplateProductSavedModalCtrl',
                                template: require('../../../../views/pages/create-product/templateProductSavedModal.html'),
                                locals: {
                                    forMyProductBases: forMyProductBases,
                                    templateId: templateSaved.id
                                },
                                parent: angular.element(document.body),
                                clickOutsideToClose: false,
                                escapeToClose: false
                            })
                            .then(() => {
                                $scope.productSaved = true;
                                forMyProductBases
                                    ? $location.path('/MyProductBases')
                                    : $location.path('/pod/productBases');
                                $location.url($location.path());
                                ProductService.clearChildTemplateProduct();
                            });
                    }
                    $timeout(() => $scope.$apply());
                } else {
                    const response = await DesignStudioSaveService.processSaving(SaveAs.Template);
                    if (typeof window.DD_RUM !== 'undefined' && response && response.id) {
                        const isEditMode = ProductService.getEditMode();
                        window.DD_RUM.addAction(isEditMode ? 'template-edit' : 'template-create', {
                            templateId: response.id
                        });
                    }

                    // If template/design is saved the modal will be shown if the edition is not coming from a desing.
                    if (!isUserDesignCreation && !flowId) {
                        if ($scope.shProduct) {
                            $rootScope.shProduct.createSetFromTemplate = $scope.shProduct.createOptionSetFromTemplate;
                            $scope.productSaved = true;
                            $rootScope.shProduct.templateId = response.id;
                            $rootScope.shProduct.templateName = product.name;
                            blockUI.stop();
                            $timeout(() =>
                                $scope.$apply(function () {
                                    $location.path('/store/product');
                                })
                            );
                        } else {
                            blockUI.stop();
                            $timeout(() => $scope.$apply());
                            $scope.saveTemplateDetails = {
                                ...$scope.saveTemplateDetails,
                                isOpen: true,
                                productTitle: product.name,
                                productId: product.id
                            };

                            if (
                                !$rootScope?.user?.AccessRoles?.Serialized?.split(',')?.includes(
                                    'skip-save-testing-values'
                                )
                            ) {
                                const savedValues = dropImageReplicaTestValues(
                                    ProductService.getElementsIdAndTestingValues() || [],
                                    ProductService.getElementsLinks()
                                );
                                if (savedValues?.length) {
                                    $scope.IndexDbService.saveValues(product.id, savedValues);
                                }
                            }
                        }
                    } else {
                        blockUI.stop();
                        $timeout(() => $scope.$apply());
                        $scope.productSaved = true;
                        $location.path('/store/designLibrary/').search({
                            forUserDesignCreation: isUserDesignCreation,
                            flowId: flowId,
                            templateCreated: product.id
                        });
                    }
                }
            } catch (error) {
                // Releasing in finally: reporting or $apply throwing here used to skip the stop
                // below, leaving the block counter up and the UI gated on it stuck for good.
                try {
                    window.rumReportError(error);
                    ProductService.clearChildTemplateProduct();
                    $location.url($location.path());
                    $scope.$apply();
                } finally {
                    blockUI.stop();
                    $timeout(() => $scope.$apply());
                }
            }
        };

        $scope.handleSaveTemplateNavigateToHome = function () {
            $scope.goHome();
        };

        $scope.handleSaveTemplateNavigateToTemplates = function (id) {
            $scope.productSaved = true;
            $location.path('/store/products/').search({
                lastCreatedTemplateId: id
            });
        };

        $scope.storeChildTemplateProductParameters = function (
            childTemplateProductParameters,
            parentPrintSizeVariants,
            sizes
        ) {
            if (childTemplateProductParameters.parentTemplateProductEdition) {
                ProductService.setChildTemplateProduct({
                    ...childTemplateProductParameters,
                    oldPrintVariantName: parentPrintSizeVariants.oldPrintVariantName || parentPrintSizeVariants.name,
                    newPrintVariantName: parentPrintSizeVariants.newPrintVariantName || parentPrintSizeVariants.name,
                    forChildTemplateProductCreation: true,
                    parentTemplateProductEdition: true,
                    isCompleted: childTemplateProductParameters.isCompleted
                });
                return;
            }

            if (!childTemplateProductParameters || !childTemplateProductParameters.forChildTemplateProductCreation)
                return;

            const editedSizes = [...(sizes || [])];
            const currentSize = editedSizes.pop();
            const { width, height, unit } = childTemplateProductParameters.currentTemplate.dimension;
            const defaultPrintSizeName = 'Size';
            const defaultVariantValue = `${Number(width).toFixed(2)} x ${Number(height).toFixed(2)} ${unit}`;

            childTemplateProductParameters.currentTemplate = {
                ...childTemplateProductParameters.currentTemplate,
                newVariant:
                    currentSize?.name ??
                    childTemplateProductParameters.currentTemplate.newVariant ??
                    defaultVariantValue,
                oldVariant: childTemplateProductParameters.currentTemplate.oldVariant ?? defaultVariantValue,
                newVariantValue:
                    currentSize?.name ??
                    childTemplateProductParameters.currentTemplate.newVariant ??
                    defaultVariantValue,
                oldVariantValue: childTemplateProductParameters.currentTemplate.oldVariant ?? defaultVariantValue
            };

            const savedTemplates = (childTemplateProductParameters.savedTemplates || []).map((template, idx) => ({
                ...template,
                newVariant: editedSizes[idx]?.name || template.newVariant,
                newVariantValue: editedSizes[idx]?.name || template.newVariantValue
            }));

            ProductService.setChildTemplateProduct({
                ...childTemplateProductParameters,
                newPrintVariantName: childTemplateProductParameters.newPrintVariantName ?? defaultPrintSizeName,
                oldPrintVariantName: childTemplateProductParameters.oldPrintVariantName ?? defaultPrintSizeName,
                savedTemplates: savedTemplates.length
                    ? [...savedTemplates, childTemplateProductParameters.currentTemplate]
                    : [childTemplateProductParameters.currentTemplate]
            });

            return ProductService.getChildTemplateProduct();
        };

        $scope.goHome = function () {
            $scope.productSaved = true;
            ProductService.clearChildTemplateProduct();
            $location.url($location.path());
            $location.path('/home');
        };

        $scope.$on('$locationChangeStart', function (event, next, current) {
            // If it is design, is required to move to /store without showing this alert.
            if (!next.includes('/createProduct') && !$scope.productSaved && !isUserDesignCreation) {
                if (
                    !confirm(
                        "You're about to exit the template. This means all changes will be lost, do you want to continue?"
                    )
                ) {
                    event.preventDefault();
                    ProductService.clearChildTemplateProduct();
                } else {
                    $location.url(`${new URL(next).pathname}`);
                    ProductService.clearProduct();
                }
            }
            $rootScope.lastLocation = current;
        });

        $scope.customilyCalendar = {
            date: new Date()
        };

        $scope.onCalendarDateChange = function () {
            const date = $scope.customilyCalendar.date || new Date();

            $rootScope.$broadcast('uploadCustomilyCalendar', date);
        };

        $scope.selectElement = function (id) {
            const data = { id };
            $rootScope.$broadcast('setActiveElement:stepOne', data);
            $rootScope.$broadcast('setActiveElement:stepTwo', data);
        };

        $scope.$on('canvas:loaded:stepThree', function () {
            if ($scope.fromListing) {
                const product = ProductService.getProduct();
                const printArea = product?.preview?.imagePlaceHoldersPreview?.find((item) => item.isPrintArea);
                if (printArea) {
                    $scope.selectElement(printArea.id);
                }
            }
            $rootScope.$broadcast('canvas:loaded:stepTwo');
        });

        $scope.stepThreeLayersSettingsProps = {
            title: 'Select a layer to review',
            events: {
                broadcastEvent: $scope.broadcastEvent,
                updateLayersOrderCanvas: $scope.updateLayersOrderCanvas,
                updateSelectedElement: $scope.updateSelectedElement
            }
        };

        $scope.showBlockUiLoader = function (active, message = 'Loading...') {
            $scope.$evalAsync(() => {
                if (!active) {
                    blockUI.stop();
                    return;
                }

                if (blockUI.state().blocking) {
                    blockUI.message(message);
                    return;
                }

                blockUI.start(message);
            });
        };
    }
})();
